import { LucideIcon } from "lucide-react";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

interface StatItem {
  value: string;
  label: string;
}

interface WhyChooseUsSectionProps {
  title: string;
  subtitle: string;
  stats: StatItem[];
  accentColor?: string;
  cta?: {
    text: string;
    link: string;
    linkText: string;
  };
}

export const WhyChooseUsSection = ({
  title,
  subtitle,
  stats,
  accentColor = "blue",
  cta,
}: WhyChooseUsSectionProps) => {
  const colorClasses: Record<string, { gradient: string; border: string; stat: string }> = {
    blue: {
      gradient: "from-blue-500/10 to-cyan-500/10",
      border: "border-blue-500/20",
      stat: "text-blue-600 dark:text-blue-400",
    },
    sky: {
      gradient: "from-sky-500/10 to-blue-500/10",
      border: "border-sky-500/20",
      stat: "text-sky-600 dark:text-sky-400",
    },
    emerald: {
      gradient: "from-emerald-500/10 to-teal-500/10",
      border: "border-emerald-500/20",
      stat: "text-emerald-600 dark:text-emerald-400",
    },
    green: {
      gradient: "from-emerald-500/10 to-green-500/10",
      border: "border-emerald-500/20",
      stat: "text-emerald-600 dark:text-emerald-400",
    },
  };

  const colors = colorClasses[accentColor] || colorClasses.blue;

  return (
    <section className="py-12 md:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className={`max-w-4xl mx-auto p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl bg-gradient-to-br ${colors.gradient} border ${colors.border}`}>
            <div className="text-center mb-4 sm:mb-6 md:mb-8">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-1.5 sm:mb-2 px-2">
                {title}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground px-2">{subtitle}</p>
            </div>
            <div className={`grid grid-cols-2 md:grid-cols-${stats.length} gap-3 sm:gap-4 md:gap-6 text-center`}>
              {stats.map((stat, index) => (
                <div key={index} className="p-2 sm:p-0">
                  <div className={`text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 sm:mb-1 ${colors.stat}`}>{stat.value}</div>
                  <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimateOnScroll>

        {cta && (
          <AnimateOnScroll animation="fade-up" delay={200}>
            <div className="text-center mt-8 sm:mt-10 md:mt-12 px-2">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-3 sm:mb-4">{cta.text}</p>
              <Link
                to={cta.link}
                className={`inline-flex items-center gap-2 text-sm sm:text-base font-semibold hover:gap-3 transition-all ${colors.stat}`}
              >
                {cta.linkText} <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Link>
            </div>
          </AnimateOnScroll>
        )}
      </div>
    </section>
  );
};
